import type { Profile, RemotePeer } from "@codeshare/session";
import type { RunSessionInfo } from "@codeshare/sync";
import { BUILD_INFO } from "../build-info";
import type { RoomState } from "../session/room-controller";
import { Avatar } from "./Avatar";
import { confirmAction } from "./ConfirmDialog";
import { Diagnostics } from "./Diagnostics";
import { Icon } from "./Icon";
import { InviteCard } from "./InviteCard";

const FEATURE_NAMES: Record<string, string> = {
  doc: "shared editing",
  aware: "cursors",
  chat: "chat",
  term: "terminal sharing",
  blob: "file transfer",
};

function quality(rtt: number | undefined): { label: string; level: string } {
  if (rtt === undefined) return { label: "Measuring connection…", level: "unknown" };
  if (rtt < 100) return { label: `Good connection · ${rtt} ms`, level: "good" };
  if (rtt < 300) return { label: `OK connection · ${rtt} ms`, level: "ok" };
  return { label: `Slow connection · ${rtt} ms`, level: "poor" };
}

const STATE_LABEL: Record<string, string> = {
  connecting: "Connecting…",
  authenticating: "Checking the room key…",
  ready: "Connected",
};

interface PeoplePanelProps {
  state: RoomState;
  self: Profile;
  /** Programs people in the room are running. */
  sessions: RunSessionInfo[];
  /** Sessions whose terminal can be watched here. */
  watchable: string[];
  onWatch: (sessionId: string) => void;
  onLeave: () => void;
  /** Leave and delete this device's copy of the room's files and chat. */
  onForget: () => void;
}

export function PeoplePanel(props: PeoplePanelProps) {
  const { state, self, sessions, watchable, onWatch, onLeave, onForget } = props;
  if (state.status === "solo") {
    return (
      <p className="panel-hint">
        You&apos;re not in a room. Click Share to start one and invite someone.
      </p>
    );
  }
  return (
    <div className="people">
      <p data-testid="room-status">
        {state.status === "joining" && "Joining…"}
        {state.status === "joined" && `In room as ${self.name}`}
        {state.status === "error" && `Couldn't join: ${state.error}`}
      </p>
      {state.warnings.length > 0 && (
        <div className="config-warnings" role="status">
          <p>Some connection settings were ignored:</p>
          <ul>
            {state.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      <h3 className="panel-subheading">People</h3>
      <ul className="people-list" aria-label="Peers">
        <li className="peer self">
          <Avatar name={self.name} color={self.color} />
          <span className="peer-name">{self.name} (you)</span>
          <span className="peer-meta">This device · v{BUILD_INFO.version}</span>
        </li>
        {state.peers.map((peer) => (
          <PeerRow key={peer.id} peer={peer} />
        ))}
      </ul>
      {sessions.length > 0 && (
        <>
          <h3 className="panel-subheading">Running</h3>
          <ul className="session-list" aria-label="Run sessions">
            {sessions.map((s) => (
              <li key={s.id} className="session-item" data-status={s.status}>
                <span className="session-file">
                  <Icon name="terminal" size={14} />
                  {s.file}
                </span>
                <span className="peer-meta">
                  {s.hostName}
                  {s.status === "exited" ? ` · exited (${s.exitCode ?? "?"})` : " · running"}
                </span>
                {watchable.includes(s.id) && (
                  <button
                    type="button"
                    className="link-button"
                    aria-label={`Watch ${s.file} (${s.hostName})`}
                    onClick={() => onWatch(s.id)}
                  >
                    Watch
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {state.peers.length === 0 && state.status === "joined" && (
        <p className="panel-hint">Waiting for others to join…</p>
      )}
      {(state.unreachable ?? 0) > 0 && (
        <p className="panel-hint unreachable-hint" data-testid="unreachable">
          Couldn't connect to {state.unreachable === 1 ? "1 person" : `${state.unreachable} people`}
          : a network between you blocks direct connections, and the fallback relay didn't help.
          Check the fallback relay or add a TURN server (Connection settings).
        </p>
      )}
      {state.link && <InviteCard link={state.link} />}
      <Diagnostics />
      <div className="leave-actions">
        <button type="button" onClick={onLeave} title="Keep a copy of the files on this device">
          Leave room
        </button>
        <button
          type="button"
          className="danger"
          title="Delete this device's copy of the room's files and chat"
          onClick={() =>
            void confirmAction({
              title: "Leave and forget this room?",
              detail:
                "This device's copy of the room's files and chat is deleted. Others in the room keep theirs.",
              confirmLabel: "Leave & forget",
              danger: true,
            }).then((confirmed) => confirmed && onForget())
          }
        >
          Leave &amp; forget
        </button>
      </div>
    </div>
  );
}

function PeerRow({ peer }: { peer: RemotePeer }) {
  const unavailable = Object.entries(peer.channels)
    .filter(([, v]) => v === null)
    .map(([name]) => FEATURE_NAMES[name] ?? name);
  const q = quality(peer.rtt);
  // Builds only matter when they differ (protocol details live in Connection details).
  const otherBuild =
    peer.app && peer.app.version !== BUILD_INFO.version ? peer.app.version : undefined;
  return (
    <li className="peer" data-state={peer.state}>
      <Avatar name={peer.profile?.name ?? "?"} color={peer.profile?.color} />
      <span className="peer-name">{peer.profile?.name ?? "Connecting…"}</span>
      <span className="peer-meta">
        {peer.state === "ready" ? (
          <span className="connection-quality" data-level={q.level}>
            {q.label}
          </span>
        ) : (
          (STATE_LABEL[peer.state] ?? peer.state)
        )}
        {peer.transport === "iroh" && (
          <span title="A direct connection failed; this one goes through a relay">
            {" "}
            · via relay
          </span>
        )}
        {otherBuild && ` · v${otherBuild}`}
      </span>
      {unavailable.length > 0 && (
        <span className="peer-meta" data-testid="unavailable-features">
          Not available with {peer.profile?.name ?? "this peer"} (different versions):{" "}
          {unavailable.join(", ")}
        </span>
      )}
    </li>
  );
}
